import { Autocomplete, Button, Modal, Typography } from '@mui/material';
import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import {
  EditEntry,
  EditEntryForm,
  EntryActions,
  InputWrapperWithLabel,
  LastModifiedContainer,
  TitleWrapper,
  UpdateEntryModalWrapper,
} from './UpdateEntryModal.styled';
import { ReactComponent as XIcon } from '../../../../../assets/icons/base/x.svg';
import HistoryIcon from '@mui/icons-material/History';
import { BasicInput } from '../../../../../components/common/InputDropdown';
import { InputHotlistWithLabel } from '../SingleHotlist.styled';
import { AutoCompleteInput } from '../../../../../components/HotlistModal/HotlistModal.styles';
import { DefaultFieldsType } from '../SingleHotlist.types';
import {
  AlertTypeType,
  RegionType,
} from '../../../../../components/HotlistModal/HotlistModal.types';
import { Entry } from '../../../../../api/paths.types';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import api from '../../../../../api/base';
import { entryUpdate } from '../../../../../api/paths';
import { CloseModalButton } from '../../../Configuration.styled';
import useError from '../../../../../hooks/useError';
import moment from 'moment';

export type UpdateEntryModalRefType = {
  openModal: (entry: Entry) => void;
};

type UpdateEntryModalPropsType = {
  defaultFields: DefaultFieldsType;
  fetchEntries: () => void;
  deleteEntry: (entry: Entry) => void;
  onUpdate?: () => void;
};

const UpdateEntryModal = forwardRef<
  UpdateEntryModalRefType,
  UpdateEntryModalPropsType
>((props, ref) => {
  const { fetchEntries, defaultFields, deleteEntry } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [plate, setPlate] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [alertType, setAlertType] = useState<AlertTypeType | null>(null);
  const [state, setState] = useState<RegionType | null>(null);
  const [entryId, setEntryId] = useState<string>('');
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  const [openEntry, setOpenEntry] = useState<Entry | null>(null);

  const [handleError] = useError();

  useImperativeHandle(ref, () => ({
    openModal: (entry: Entry) => {
      setOpen(true);
      setOpenEntry(entry);
      setName(`${entry.created_by.first_name} ${entry.created_by.last_name}`);
      setDate(moment(entry.modified).format('MM/DD/YY, hh:mm A'));
      setPlate(entry.plate);
      setNotes(entry.notes);
      setEntryId(entry.id);

      const alertTypeFull = defaultFields.alertTypes.find(
        (at) => at.id === entry.alert_type.id,
      );
      const stateFull = defaultFields.states.find(
        (st) => st.id === entry.region.id,
      );
      setAlertType(alertTypeFull!);
      setState(stateFull!);
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setOpenEntry(null);
    setPlate('');
    setNotes('');
    setAlertType(null);
    setState(null);
    setUpdateLoading(false);
    fetchEntries();
  };

  const deleteFromInside = async () => {
    if (openEntry) {
      await deleteEntry(openEntry);
      if (props.onUpdate) props?.onUpdate();
      handleClose();
    }
  };

  const updateEntry = async () => {
    setUpdateLoading(true);
    try {
      await api.patch(`${entryUpdate}${entryId}/`, {
        plate,
        region: state?.id,
        alert_type: alertType?.id,
        notes,
      });
      if (props.onUpdate) props?.onUpdate();

      // We want to refetch entries to ensure data integrity
      // But ES is not that fast with updates
      // If we won't do delay - we will get old data
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error: any) {
      handleError(error);
      setUpdateLoading(false);
    }
  };

  const handleStateChange = (e: any, value: any, reason: string) => {
    setState(reason !== 'clear' ? value : null);
  };

  const handleAlertChange = (e: any, value: any, reason: string) => {
    setAlertType(reason !== 'clear' ? value : null);
  };

  const handlePlateChange = (e: any) => {
    const {
      target: { value },
    } = e;

    setPlate(value);
  };

  const handleNotesChange = (e: any) => {
    const {
      target: { value },
    } = e;

    setNotes(value);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <UpdateEntryModalWrapper>
        <EditEntry>
          <TitleWrapper>
            <Typography variant="txl" fontWeight="500" color="grey.900">
              Edit Entry
            </Typography>
            <CloseModalButton
              onClick={handleClose}
              sx={{ width: '30px', height: '30px' }}
            >
              <XIcon />
            </CloseModalButton>
          </TitleWrapper>
        </EditEntry>
        <EditEntryForm>
          <LastModifiedContainer>
            <span>
              <HistoryIcon sx={{ stroke: 'red' }} />
              Last updated by: <b>{name}</b>
            </span>
            <span>
              at <b>{date}</b>
            </span>
          </LastModifiedContainer>
          <InputWrapperWithLabel>
            <Typography color="grey.700" variant="tsm" fontWeight="500">
              License Plate
            </Typography>
            <BasicInput
              error={!plate}
              value={plate}
              onChange={handlePlateChange}
            />
          </InputWrapperWithLabel>
          <InputHotlistWithLabel flexGrow="1">
            <Typography color="grey.700" variant="tsm" fontWeight="500">
              State
            </Typography>
            <Autocomplete
              disablePortal
              options={defaultFields.states}
              sx={{ width: '100%', height: 44 }}
              getOptionLabel={(option: any) => option.name}
              value={state}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id
              }
              onChange={handleStateChange}
              renderInput={(params) => (
                <AutoCompleteInput
                  error={!state}
                  {...params}
                  placeholder="Select state"
                />
              )}
            />
          </InputHotlistWithLabel>
          <InputHotlistWithLabel flexGrow="1">
            <Typography color="grey.700" variant="tsm" fontWeight="500">
              Alarm Class
            </Typography>
            <Autocomplete
              disablePortal
              options={defaultFields.alertTypes}
              sx={{ width: '100%', height: 44 }}
              value={alertType}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id
              }
              onChange={handleAlertChange}
              renderInput={(params) => (
                <AutoCompleteInput
                  error={!alertType}
                  {...params}
                  placeholder="Select Alarm Class"
                />
              )}
            />
          </InputHotlistWithLabel>
          <InputWrapperWithLabel>
            <Typography color="grey.700" variant="tsm" fontWeight="500">
              Notes
            </Typography>
            <BasicInput
              error={!notes}
              value={notes}
              multiline
              rows={4}
              onChange={handleNotesChange}
            />
          </InputWrapperWithLabel>
        </EditEntryForm>
        <EntryActions>
          <LoadingButton
            variant="outlined"
            color="error"
            onClick={deleteFromInside}
          >
            Delete
          </LoadingButton>
          <Box display="flex" gap="12px">
            <Button size="large" variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              size="large"
              variant="contained"
              onClick={updateEntry}
              loading={updateLoading}
            >
              Update
            </LoadingButton>
          </Box>
        </EntryActions>
      </UpdateEntryModalWrapper>
    </Modal>
  );
});

export default memo(UpdateEntryModal);
